import { Fragment, useState, useEffect, useMemo, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { Dialog, DialogPanel, Transition } from '@headlessui/react'
import StripePaymentModal from './StripePaymentWrapper'
import BookingSuccessModal from './BookingSuccessModal'
import { convertAvailabilityFromGMT, getUserTimezone } from '../utils/timezoneUtils'
import { useAuth } from '../hooks/useAuth'
import { useUserProfile } from '../hooks/useUserProfile'
import { isProfessionalBookingEligible, validateProfessionalStripeAccount, Professional as ProfessionalType } from '../lib/professionalService'
import { getProfessionalBookedMeetings } from '../lib/meetingService'

// Extend Window interface for custom properties
declare global {
  interface Window {
    lastSuccessfulPaymentId?: string;
  }
}

interface TimeSlot {
  start: string
  end: string
}

interface DayAvailability {
  enabled: boolean
  timeSlots: TimeSlot[]
}

interface WeeklyAvailability {
  monday: DayAvailability
  tuesday: DayAvailability
  wednesday: DayAvailability
  thursday: DayAvailability
  friday: DayAvailability
  saturday: DayAvailability
  sunday: DayAvailability
}

interface BookingCalendarProps {
  open: boolean
  onClose: () => void
  professional: ProfessionalType | null
  onNavigateToCalendar?: () => void
}

const DAY_KEYS: (keyof WeeklyAvailability)[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const DOW_TO_KEY: Record<number, keyof WeeklyAvailability> = {
  0: 'sunday', 1: 'monday', 2: 'tuesday', 3: 'wednesday',
  4: 'thursday', 5: 'friday', 6: 'saturday'
}

// ─── Helpers ───
function to12h(h: number, m: number): string {
  const period = h >= 12 ? 'PM' : 'AM'
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${h12}:${m.toString().padStart(2, '0')} ${period}`
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function fmtDayLong(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
}

function fmtDateShort(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function fmtMonthYear(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

type SlotPeriod = 'all' | 'morning' | 'afternoon' | 'evening'

function periodOf(h: number): 'morning' | 'afternoon' | 'evening' {
  if (h < 12) return 'morning'
  if (h < 17) return 'afternoon'
  return 'evening'
}

function getTzAbbreviation(tz: string): string {
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
    const parts = formatter.formatToParts(now)
    return parts.find(p => p.type === 'timeZoneName')?.value || ''
  } catch {
    return ''
  }
}

function getTzDisplay(tz: string): string {
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
    const parts = formatter.formatToParts(now)
    const offset = parts.find(p => p.type === 'timeZoneName')?.value || ''
    return `${tz.replace(/_/g, ' ')} · ${offset}`
  } catch {
    return tz
  }
}

// ─── Component ───
export default function BookingCalendar({
  open,
  onClose,
  professional,
  onNavigateToCalendar
}: BookingCalendarProps) {
  const { user: authUser } = useAuth()
  const { userProfile } = useUserProfile()
  const userTimezone = getUserTimezone(userProfile?.timezone)

  // ── State ──
  const [step, setStep] = useState(1)
  const [currentMonth, setCurrentMonth] = useState<Date>(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1))
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTime, setSelectedTime] = useState<string | null>(null) // e.g. "9:00 AM"
  const [slotPeriod, setSlotPeriod] = useState<SlotPeriod>('all')
  const [clientAvailability, setClientAvailability] = useState<WeeklyAvailability | null>(null)
  const [bookedMeetings, setBookedMeetings] = useState<{ startTime: Date; endTime: Date; meetingId: string }[]>([])

  // Details step
  const [detailsFirstName, setDetailsFirstName] = useState('')
  const [detailsLastName, setDetailsLastName] = useState('')
  const [detailsEmail, setDetailsEmail] = useState('')
  const [detailsTopic, setDetailsTopic] = useState('')
  const [detailsReminder, setDetailsReminder] = useState(true)

  // Payment flow
  const [showPayment, setShowPayment] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [isValidatingStripe, setIsValidatingStripe] = useState(false)
  const [stripeValidationError, setStripeValidationError] = useState<string | null>(null)

  // ── Effects ──
  // Convert professional's GMT availability to client timezone
  useEffect(() => {
    if (professional?.availability) {
      const converted = convertAvailabilityFromGMT(professional.availability, userTimezone)
      setClientAvailability(converted)
    } else {
      setClientAvailability(null)
    }
  }, [professional?.availability, userTimezone])

  // Fetch booked meetings for conflict detection
  useEffect(() => {
    const fetchBookedMeetings = async () => {
      if (!professional?.uid) return
      // Fetch for a broad date range (current month ± 1)
      const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
      const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0, 23, 59, 59)
      try {
        const meetings = await getProfessionalBookedMeetings(professional.uid, start, end)
        setBookedMeetings(meetings)
      } catch (error) {
        console.error('Error fetching booked meetings:', error)
        setBookedMeetings([])
      }
    }
    if (open && professional?.uid) {
      fetchBookedMeetings()
    }
  }, [open, professional?.uid, currentMonth])

  // Pre-fill user details
  useEffect(() => {
    if (open && authUser) {
      setDetailsFirstName(userProfile?.firstName || '')
      setDetailsLastName(userProfile?.lastName || '')
      setDetailsEmail(authUser.email || userProfile?.email || '')
    }
  }, [open, authUser, userProfile])

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setStep(1)
      setCurrentMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))
      setSelectedDate(null)
      setSelectedTime(null)
      setSlotPeriod('all')
      setDetailsTopic('')
      setDetailsReminder(true)
      setStripeValidationError(null)
    }
  }, [open])

  // ── Availability logic ──
  // Which days of the week does this pro work? Returns Set of JS day numbers (0=Sun)
  const workingDays = useMemo<Set<number>>(() => {
    const result = new Set<number>()
    if (!clientAvailability) return result
    const dayNumMap: Record<string, number> = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
      thursday: 4, friday: 5, saturday: 6
    }
    DAY_KEYS.forEach(key => {
      const day = clientAvailability[key]
      if (day?.enabled && day.timeSlots?.length > 0) {
        result.add(dayNumMap[key])
      }
    })
    return result
  }, [clientAvailability])

  // Check if a specific 30-min slot conflicts with booked meetings
  const hasBookingConflict = useCallback((date: Date, hour: number, minute: number): boolean => {
    if (bookedMeetings.length === 0) return false
    const slotStart = new Date(date)
    slotStart.setHours(hour, minute, 0, 0)
    const slotEnd = new Date(slotStart.getTime() + 30 * 60 * 1000)

    return bookedMeetings.some(meeting => {
      const meetingStartInClientTZ = new Date(meeting.startTime.toLocaleString('sv-SE', { timeZone: userTimezone }))
      const meetingEndInClientTZ = new Date(meeting.endTime.toLocaleString('sv-SE', { timeZone: userTimezone }))
      return slotStart < meetingEndInClientTZ && slotEnd > meetingStartInClientTZ
    })
  }, [bookedMeetings, userTimezone])

  // Generate available 30-min time slots for a given date
  const slotsForDate = useCallback((date: Date): { label: string; h: number; m: number }[] => {
    if (!clientAvailability) return []
    const dayKey = DOW_TO_KEY[date.getDay()]
    const dayData = clientAvailability[dayKey]
    if (!dayData?.enabled || !dayData.timeSlots?.length) return []

    const now = new Date()
    const isToday = sameDay(date, now)
    const slots: { label: string; h: number; m: number }[] = []

    dayData.timeSlots.forEach((ts: TimeSlot) => {
      const [startH, startM] = ts.start.split(':').map(Number)
      const [endH, endM] = ts.end.split(':').map(Number)
      const startMins = startH * 60 + startM
      const endMins = endH * 60 + endM

      for (let mins = startMins; mins + 30 <= endMins; mins += 30) {
        const h = Math.floor(mins / 60)
        const m = mins % 60

        // Skip past times for today
        if (isToday && (h < now.getHours() || (h === now.getHours() && m <= now.getMinutes()))) continue

        // Skip booked slots
        if (hasBookingConflict(date, h, m)) continue

        slots.push({ label: to12h(h, m), h, m })
      }
    })

    return slots
  }, [clientAvailability, hasBookingConflict])

  // ── Calendar grid ──
  const calendarCells = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstOfMonth = new Date(year, month, 1)
    const startDay = (firstOfMonth.getDay() + 6) % 7 // 0=Monday
    const gridStart = new Date(firstOfMonth)
    gridStart.setDate(firstOfMonth.getDate() - startDay)

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const cells: { date: Date; inMonth: boolean; isOff: boolean; isAvailable: boolean; isToday: boolean }[] = []
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart)
      d.setDate(gridStart.getDate() + i)
      const inMonth = d.getMonth() === month
      const isPast = d < today
      const isWorkDay = workingDays.has(d.getDay())
      const isToday_ = sameDay(d, today)

      cells.push({
        date: d,
        inMonth,
        isOff: !inMonth || isPast || !isWorkDay,
        isAvailable: inMonth && !isPast && isWorkDay,
        isToday: isToday_
      })
    }
    return cells
  }, [currentMonth, workingDays])

  // All slots for selected date
  const currentSlots = useMemo(() => {
    if (!selectedDate) return []
    return slotsForDate(selectedDate)
  }, [selectedDate, slotsForDate])

  // Filtered slots by period
  const filteredSlots = useMemo(() => {
    if (slotPeriod === 'all') return currentSlots
    return currentSlots.filter(s => periodOf(s.h) === slotPeriod)
  }, [currentSlots, slotPeriod])

  // ── Handlers ──
  const handleDayClick = (cell: typeof calendarCells[0]) => {
    if (cell.isOff) return
    setSelectedDate(cell.date)
    setSelectedTime(null)
    setSlotPeriod('all')
  }

  const handleSlotClick = (label: string) => {
    setSelectedTime(label)
  }

  const handleContinueStep1 = () => {
    if (selectedDate && selectedTime) setStep(2)
  }

  const handleContinueStep2 = () => {
    setStep(3)
  }

  const handleProceedToPayment = async () => {
    if (!professional) return
    setIsValidatingStripe(true)
    setStripeValidationError(null)

    try {
      if (!isProfessionalBookingEligible({
        uid: professional.uid,
        stripeAccountId: professional.stripeAccountId,
        isStripeOnboarded: professional.isStripeOnboarded
      } as ProfessionalType)) {
        setStripeValidationError('This professional is not yet set up to receive payments. Please contact them to complete their account setup.')
        setIsValidatingStripe(false)
        return
      }

      const validation = await validateProfessionalStripeAccount(professional.uid)
      if (!validation.isValid) {
        setStripeValidationError(validation.error || 'This professional\'s payment account is not properly configured. Please try again later or contact support.')
        setIsValidatingStripe(false)
        return
      }

      // All good — open Stripe payment
      setShowPayment(true)
    } catch {
      setStripeValidationError('Unable to verify payment setup. Please try again later.')
    } finally {
      setIsValidatingStripe(false)
    }
  }

  const handlePaymentSuccess = async () => {
    if (selectedDate && selectedTime && professional && authUser) {
      try {
        const { httpsCallable } = await import('firebase/functions')
        const { functions } = await import('../lib/firebase')
        const confirmBookingFn = httpsCallable(functions, 'confirmBooking')

        const paymentIntentId = window.lastSuccessfulPaymentId
        if (!paymentIntentId) throw new Error('Payment intent ID not found')

        await confirmBookingFn({
          paymentIntentId,
          clientId: authUser.uid,
          clientName: userProfile?.firstName && userProfile?.lastName
            ? `${userProfile.firstName} ${userProfile.lastName}`
            : authUser.displayName || 'Client',
          clientEmail: authUser.email || userProfile?.email || 'client@example.com',
          professionalName: professional.displayName,
          professionalEmail: professional.email || 'professional@example.com',
          professionalTitle: professional.title || professional.category || 'Professional'
        })

        flushSync(() => { setShowSuccess(true) })
        setShowPayment(false)
      } catch {
        flushSync(() => { setShowSuccess(true) })
        setShowPayment(false)
      }
    }
  }

  const handleClosePayment = () => {
    setShowPayment(false)
  }

  const handleCloseSuccess = () => {
    setShowSuccess(false)
    onClose()
  }

  // ── Derived display values ──
  const tzAbbr = getTzAbbreviation(userTimezone)
  const tzDisplay = getTzDisplay(userTimezone)
  const sessionRate = professional?.sessionRate || 0
  const ready = !!selectedDate && !!selectedTime

  // Session detail string for StripePaymentWrapper
  const sessionDetails = selectedDate && selectedTime ? {
    date: fmtDayLong(selectedDate),
    time: selectedTime,
    duration: 30
  } : { date: '', time: '', duration: 30 }

  if (!professional) return null

  const specLabel = (professional.specialization || professional.title || 'Engineer')
    .replace(' Engineer', '').replace(' Developer', '')

  return (
    <>
      <Transition appear show={open && !showSuccess} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => {
          if (!showPayment) onClose()
        }}>
          <Transition.Child as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(12px) saturate(120%)' }} />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto px-4 py-6 sm:px-6 sm:py-10">
            <div className="flex min-h-full items-start justify-center">
              <Transition.Child as={Fragment}
                enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2 scale-[0.985]" enterTo="opacity-100 translate-y-0 scale-100"
                leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 scale-100" leaveTo="opacity-0 translate-y-2 scale-[0.985]"
              >
                <DialogPanel
                  className="w-full max-w-[1000px] overflow-hidden rounded-2xl md:rounded-[20px] relative"
                  style={{ background: 'hsl(0 0% 5%)', border: '1px solid hsl(0 0% 20%)', boxShadow: '0 30px 80px rgba(0,0,0,0.55)' }}
                >
                  {/* Close button */}
                  <button
                    onClick={onClose}
                    className="absolute top-3 right-3 md:top-[18px] md:right-[18px] w-[34px] h-[34px] rounded-full inline-flex items-center justify-center z-10 cursor-pointer transition-colors"
                    style={{ background: 'rgba(0,0,0,0.45)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.16)', backdropFilter: 'blur(6px)' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </button>

                  {/* ── Header ── */}
                  <div className="px-6 md:px-8 pt-6 pb-4 flex items-center justify-between gap-6"
                    style={{ borderBottom: '1px solid hsl(0 0% 20% / 0.6)' }}>
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-12 h-12 rounded-xl grid place-items-center text-[14px] font-semibold shrink-0 overflow-hidden"
                        style={{ background: 'linear-gradient(135deg, hsl(0 0% 28%), hsl(0 0% 9%))', boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
                        {professional.profileImage ? (
                          <img src={professional.profileImage} alt="" className="w-full h-full object-cover" />
                        ) : (
                          professional.displayName.split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[17px] font-semibold tracking-tight truncate">Book with {professional.displayName}</div>
                        <div className="text-white/50 text-[12.5px] truncate">
                          {specLabel}{professional.company && <> · <span className="text-white/85">{professional.company}</span></>}
                        </div>
                      </div>
                    </div>
                    <div className="hidden md:flex items-center gap-2 text-[11.5px] text-white/50">
                      <span>30 min</span>
                      <span className="opacity-40">·</span>
                      <span className="text-white/90 font-medium">${sessionRate}</span>
                      <span className="opacity-40">·</span>
                      <span>Video call</span>
                    </div>
                  </div>

                  {/* ── Stepper ── */}
                  <div className="px-6 md:px-8 py-3 flex items-center gap-4 sm:gap-6 text-[12px] overflow-x-auto"
                    style={{ borderBottom: '1px solid hsl(0 0% 20% / 0.5)', background: 'rgba(255,255,255,0.015)' }}>
                    {[
                      { n: 1, label: 'Pick a time' },
                      { n: 2, label: 'Your details' },
                      { n: 3, label: 'Pay & confirm' },
                    ].map(({ n, label }, i) => (
                      <Fragment key={n}>
                        {i > 0 && <span className="text-white/25">→</span>}
                        <span className={`flex items-center gap-2 ${step === n ? 'text-white' : step > n ? 'text-white' : 'text-white/50'}`}>
                          <span className={`step-num-pill ${step === n ? 'step-num-pill-active' : ''} ${step > n ? 'step-num-pill-done' : ''}`}>
                            {step > n ? (
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6"/></svg>
                            ) : n}
                          </span>
                          <span className={`font-medium whitespace-nowrap ${step === n ? '' : ''}`}>{label}</span>
                        </span>
                      </Fragment>
                    ))}
                  </div>

                  {/* ═══════ STEP 1: Pick a time ═══════ */}
                  {step === 1 && (
                    <div className="grid md:grid-cols-[1fr_320px]">
                      {/* Calendar + slots */}
                      <div className="p-6 md:p-8 min-w-0">
                        {/* Month nav */}
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <div className="text-[18px] font-medium tracking-tight">{fmtMonthYear(currentMonth)}</div>
                            <div className="text-white/50 text-[11.5px] mt-0.5">{tzDisplay}</div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button className="btn btn-ghost !px-2.5 !py-2" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
                            </button>
                            <button className="btn btn-ghost !px-2.5 !py-2" onClick={() => setCurrentMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}>Today</button>
                            <button className="btn btn-ghost !px-2.5 !py-2" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
                            </button>
                          </div>
                        </div>

                        {/* Calendar grid */}
                        <div>
                          <div className="cal-grid">
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                              <div key={d} className="cal-dow">{d}</div>
                            ))}
                          </div>
                          <div className="cal-grid">
                            {calendarCells.map((cell, i) => {
                              const isSelected = selectedDate && sameDay(cell.date, selectedDate)
                              let cls = 'cal-day'
                              if (!cell.inMonth) cls += ' cal-day-other'
                              else if (cell.isOff) cls += ' cal-day-off'
                              else if (isSelected) cls += ' cal-day-selected'
                              else cls += ' cal-day-available'
                              if (cell.isToday && !isSelected) cls += ' cal-day-today'

                              return (
                                <div
                                  key={i}
                                  className={cls}
                                  onClick={() => handleDayClick(cell)}
                                >
                                  <span>{cell.date.getDate()}</span>
                                  {(cell.isAvailable || isSelected) && <span className="cal-dot" />}
                                </div>
                              )
                            })}
                          </div>
                        </div>

                        {/* Day legend */}
                        <div className="flex items-center gap-5 mt-5 text-[11px] text-white/50">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-1 h-1 rounded-full bg-white" /> Has availability
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-sm" style={{ boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }} /> Selectable
                          </span>
                        </div>

                        {/* Slot list */}
                        <div className="mt-7 pt-6" style={{ borderTop: '1px solid hsl(0 0% 20% / 0.5)' }}>
                          <div className="flex items-start sm:items-center justify-between mb-4 flex-col sm:flex-row gap-3">
                            <div>
                              <div className="text-[14px] font-medium">
                                {selectedDate ? fmtDayLong(selectedDate) : 'Pick a day to see times'}
                              </div>
                              {selectedDate && (
                                <div className="text-white/50 text-[11.5px] mt-0.5">
                                  {currentSlots.length > 0
                                    ? `${currentSlots.length} sessions available · 30 min each`
                                    : 'No availability this day'}
                                </div>
                              )}
                            </div>
                            {selectedDate && currentSlots.length > 0 && (
                              <div className="flex items-center gap-1.5 text-[11.5px]">
                                {(['all', 'morning', 'afternoon', 'evening'] as SlotPeriod[]).map(p => (
                                  <button
                                    key={p}
                                    className={`filter-chip !px-2.5 !py-1 !text-[11px] ${slotPeriod === p ? 'filter-chip-active' : ''}`}
                                    onClick={() => setSlotPeriod(p)}
                                  >
                                    {p.charAt(0).toUpperCase() + p.slice(1)}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {selectedDate && filteredSlots.length > 0 && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                              {filteredSlots.map(s => (
                                <button
                                  key={s.label}
                                  className={`book-slot ${selectedTime === s.label ? 'book-slot-selected' : ''}`}
                                  onClick={() => handleSlotClick(s.label)}
                                >
                                  {s.label}
                                </button>
                              ))}
                            </div>
                          )}

                          {selectedDate && filteredSlots.length === 0 && currentSlots.length > 0 && (
                            <div className="text-center py-10 text-white/50 text-[12.5px]">
                              No times match that filter. Try another period or another day.
                            </div>
                          )}

                          {selectedDate && currentSlots.length === 0 && (
                            <div className="text-center py-10 text-white/50 text-[12.5px]">
                              No available time slots for this day.
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Side summary */}
                      <div className="p-6 md:p-8 flex flex-col gap-5"
                        style={{ background: 'rgba(255,255,255,0.015)', borderLeft: '1px solid hsl(0 0% 20% / 0.5)' }}>
                        <div className="text-[11px] tracking-widest uppercase text-white/50">Session summary</div>

                        <div>
                          <div className="summary-row">
                            <span className="summary-lbl">SESSION</span>
                            <span>1:1 video call · 30 min</span>
                          </div>
                          <div className="summary-row">
                            <span className="summary-lbl">WITH</span>
                            <span className="font-medium">{professional.displayName}</span>
                          </div>
                          <div className="summary-row">
                            <span className="summary-lbl">DATE</span>
                            <span className={selectedDate ? '' : 'text-white/50 italic'}>
                              {selectedDate ? fmtDayLong(selectedDate) : 'Choose a day'}
                            </span>
                          </div>
                          <div className="summary-row">
                            <span className="summary-lbl">TIME</span>
                            <span className={selectedTime ? '' : 'text-white/50 italic'}>
                              {selectedTime ? `${selectedTime} ${tzAbbr}` : 'Pick a slot'}
                            </span>
                          </div>
                          <div className="summary-row">
                            <span className="summary-lbl">TOTAL</span>
                            <span className="font-semibold tracking-tight text-[15px]">${sessionRate}.00</span>
                          </div>
                        </div>

                        <div className="mt-auto pt-3">
                          <button
                            className="btn btn-primary w-full justify-center !py-3"
                            disabled={!ready}
                            style={{ opacity: ready ? 1 : 0.45, cursor: ready ? 'pointer' : 'not-allowed' }}
                            onClick={handleContinueStep1}
                          >
                            Continue
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                          </button>
                          <p className="text-white/50 text-[11px] mt-3 leading-relaxed">
                            You won't be charged until your session is confirmed.
                            Cancel up to 12 hours before for a full refund.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ═══════ STEP 2: Your details ═══════ */}
                  {step === 2 && (
                    <div className="grid md:grid-cols-[1fr_320px]">
                      <div className="p-6 md:p-8 flex flex-col gap-5">
                        <div>
                          <h3 className="text-[18px] font-medium tracking-tight">Your details</h3>
                          <p className="text-white/50 text-[12.5px] mt-1.5 max-w-md">
                            We'll share these with your engineer so they can prepare. Be as specific as you'd be in a Slack message.
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="label">First name</div>
                            <input className="field" value={detailsFirstName} onChange={e => setDetailsFirstName(e.target.value)} />
                          </div>
                          <div>
                            <div className="label">Last name</div>
                            <input className="field" value={detailsLastName} onChange={e => setDetailsLastName(e.target.value)} />
                          </div>
                          <div className="col-span-2">
                            <div className="label">Email</div>
                            <input className="field" type="email" value={detailsEmail} onChange={e => setDetailsEmail(e.target.value)} />
                          </div>
                        </div>

                        <div>
                          <div className="label">What would you like to talk about?</div>
                          <textarea
                            className="field"
                            style={{ resize: 'vertical', minHeight: 90, lineHeight: 1.55 }}
                            placeholder="One paragraph. The more specific, the better the session — pasting a code snippet or a link is welcome."
                            value={detailsTopic}
                            onChange={e => setDetailsTopic(e.target.value)}
                          />
                          <p className="text-white/50 text-[11.5px] mt-2">Shared only with your engineer. Never used for AI training.</p>
                        </div>

                        <label className="flex items-start gap-2.5 text-[12.5px] text-white/50 cursor-pointer mt-1">
                          <input type="checkbox" className="accent-white mt-0.5" checked={detailsReminder} onChange={e => setDetailsReminder(e.target.checked)} />
                          <span>Send me a calendar invite and a reminder 15 minutes before the call.</span>
                        </label>
                      </div>

                      {/* Side summary with edit */}
                      <div className="p-6 md:p-8 flex flex-col gap-5"
                        style={{ background: 'rgba(255,255,255,0.015)', borderLeft: '1px solid hsl(0 0% 20% / 0.5)' }}>
                        <div className="flex items-center justify-between">
                          <div className="text-[11px] tracking-widest uppercase text-white/50">Session summary</div>
                          <button className="btn btn-ghost !px-2 !py-1 text-[11px]" onClick={() => setStep(1)}>Edit</button>
                        </div>

                        <div>
                          <div className="summary-row"><span className="summary-lbl">WITH</span><span className="font-medium">{professional.displayName}</span></div>
                          <div className="summary-row"><span className="summary-lbl">SESSION</span><span>1:1 video call · 30 min</span></div>
                          <div className="summary-row"><span className="summary-lbl">DATE</span><span>{selectedDate ? fmtDayLong(selectedDate) : ''}</span></div>
                          <div className="summary-row"><span className="summary-lbl">TIME</span><span>{selectedTime} {tzAbbr}</span></div>
                          <div className="summary-row"><span className="summary-lbl">TOTAL</span><span className="font-semibold tracking-tight text-[15px]">${sessionRate}.00</span></div>
                        </div>

                        <div className="mt-auto flex flex-col gap-2">
                          <button className="btn btn-primary w-full justify-center !py-3" onClick={handleContinueStep2}>
                            Continue to payment
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                          </button>
                          <button className="btn btn-ghost w-full justify-center" onClick={() => setStep(1)}>Back</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ═══════ STEP 3: Pay & confirm ═══════ */}
                  {step === 3 && (
                    <div className="grid md:grid-cols-[1fr_320px]">
                      <div className="p-6 md:p-8 flex flex-col gap-5">
                        <div>
                          <h3 className="text-[18px] font-medium tracking-tight">Payment</h3>
                          <p className="text-white/50 text-[12.5px] mt-1.5 max-w-md">
                            We hold the charge until your session is confirmed. Payouts go to your engineer the next business day after the call.
                          </p>
                        </div>

                        <div className="flex items-center gap-2 panel-soft p-3 text-[12px] text-white/50">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-white/80">
                            <rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V8a5 5 0 0 1 10 0v3"/>
                          </svg>
                          Encrypted by Stripe. We never see your card number.
                        </div>

                        {/* Stripe validation error */}
                        {stripeValidationError && (
                          <div className="panel-soft p-4 text-[12.5px]" style={{ borderColor: 'rgba(255,138,115,0.3)', background: 'rgba(255,138,115,0.04)' }}>
                            <div className="flex items-start gap-2.5">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ff8a73" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                              <span className="text-[#ff8a73]">{stripeValidationError}</span>
                            </div>
                          </div>
                        )}

                        <p className="text-white/50 text-[12.5px] mt-2">
                          Click the button below to securely enter your payment details via Stripe.
                        </p>
                      </div>

                      {/* Order summary */}
                      <div className="p-6 md:p-8 flex flex-col gap-5"
                        style={{ background: 'rgba(255,255,255,0.015)', borderLeft: '1px solid hsl(0 0% 20% / 0.5)' }}>
                        <div className="text-[11px] tracking-widest uppercase text-white/50">Order</div>
                        <div>
                          <div className="summary-row"><span className="summary-lbl">SESSION</span><span>30-min · video</span></div>
                          <div className="summary-row"><span className="summary-lbl">WITH</span><span className="font-medium">{professional.displayName}</span></div>
                          <div className="summary-row"><span className="summary-lbl">WHEN</span><span>{selectedDate ? fmtDateShort(selectedDate) : ''} · {selectedTime} {tzAbbr}</span></div>
                          <div className="summary-row"><span className="summary-lbl">SUBTOTAL</span><span>${sessionRate}.00</span></div>
                          <div className="summary-row"><span className="summary-lbl">PLATFORM</span><span className="text-white/50">Included</span></div>
                          <div className="summary-row"><span className="summary-lbl">TOTAL</span><span className="font-semibold tracking-tight text-[16px]">${sessionRate}.00</span></div>
                        </div>

                        <div className="mt-auto flex flex-col gap-2">
                          <button
                            className="btn btn-primary w-full justify-center !py-3"
                            onClick={handleProceedToPayment}
                            disabled={isValidatingStripe || !!stripeValidationError}
                            style={{ opacity: isValidatingStripe || stripeValidationError ? 0.45 : 1 }}
                          >
                            {isValidatingStripe ? (
                              <span className="flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                                Validating...
                              </span>
                            ) : (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6"/></svg>
                                Confirm & pay ${sessionRate}
                              </>
                            )}
                          </button>
                          <button className="btn btn-ghost w-full justify-center" onClick={() => setStep(2)}>Back</button>
                          <p className="text-white/50 text-[11px] mt-1 leading-relaxed text-center">
                            By confirming, you agree to Wonderstood's <a href="#" className="hover:text-white underline underline-offset-2">terms</a>.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ═══════ STEP 4: Success (inline) ═══════ */}
                  {step === 4 && (
                    <div className="p-10 text-center">
                      <div className="mx-auto w-14 h-14 rounded-full grid place-items-center mb-5"
                        style={{ background: 'rgba(255,255,255,0.05)', boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6"/></svg>
                      </div>
                      <h3 className="text-[28px] font-medium tracking-tight">You're <span className="font-serif italic font-normal">booked</span></h3>
                      <p className="text-white/50 text-[13.5px] mt-3 max-w-md mx-auto">
                        Your session with {professional.displayName} is on the calendar for {selectedDate ? fmtDateShort(selectedDate) : ''} at {selectedTime} {tzAbbr}. We've sent a confirmation and the meeting link to your email.
                      </p>
                      <div className="flex items-center justify-center gap-2 mt-7">
                        <button className="btn btn-outline">Add to Google Calendar</button>
                        <button className="btn btn-primary" onClick={onClose}>Done</button>
                      </div>
                    </div>
                  )}
                </DialogPanel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Stripe Payment Modal (external) */}
      {showPayment && selectedDate && selectedTime && (
        <StripePaymentModal
          open={showPayment && !showSuccess}
          onClose={handleClosePayment}
          onSuccess={handlePaymentSuccess}
          amount={sessionRate}
          professionalId={professional.uid}
          professionalName={professional.displayName}
          sessionDetails={sessionDetails}
          userTimezone={userTimezone}
        />
      )}

      {/* Booking Success Modal */}
      {showSuccess && selectedDate && selectedTime && (
        <BookingSuccessModal
          open={showSuccess}
          onClose={handleCloseSuccess}
          onNavigateToCalendar={onNavigateToCalendar}
          professionalName={professional.displayName}
          sessionDetails={sessionDetails}
          amount={sessionRate}
        />
      )}
    </>
  )
}
